import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { blogImages } from '../helpers/blogImages';
import getBlogData from '../helpers/blogData';
import './Blog.css';

const BlogArticle = () => {
  const { slug } = useParams();
  const { t, i18n: i18nInstance } = useTranslation(['blog', 'home']);

  const article = i18n.getResource(i18nInstance.language, 'blogData', slug);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const images = blogImages[slug] || {};

  const relatedPosts = getBlogData()
    .filter((p) => p.slug !== slug)
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{article.title} | JetPeel</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={t('helmet.keywords')} />
        <link rel="canonical" href={`https://jetpeel.fi/blog/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={`https://jetpeel.fi/blog/${slug}`} />
        <meta property="og:image" content="https://jetpeel.fi/img/og-image.jpg" />
      </Helmet>

      <div className="blog-article">
        <div className="header__banner-wrapper"></div>
        <div className="blog-article__cover">
          <img
          loading="lazy"
          className="header__banner"
          src={images.cover}
          alt={article.title}
        />
        </div>

        <div className="container">
          <div className="blog-article__layout">

            <div className="blog-article__main">
              <Link to="/blog" className="blog-article__back">
                ← {t('page.backToBlog')}
              </Link>

              <header className="blog__header">
                <p className="blog__meta">
                  {article.date} &nbsp;·&nbsp; {article.readTime}
                </p>
                <h1 className="blog__title">{article.title}</h1>
              </header>

              <article className="blog__article">

                <div className="blog__lead">
                  {(article.lead || []).map((p, i) => <p key={i}>{p}</p>)}
                </div>

                {(article.sections || []).map((section, i) => (
                  <section key={i} className="blog__section">
                    <h2>{section.title}</h2>
                    {section.content && <p>{section.content}</p>}
                    {section.subheading && (
                      <p className="blog__subheading">{section.subheading}</p>
                    )}
                    {section.list?.length > 0 && (
                      <ul className="blog__list">
                        {section.list.map((item, j) => <li key={j}>{item}</li>)}
                      </ul>
                    )}
                    {section.contentAfter && <p>{section.contentAfter}</p>}
                    {section.listAfter?.length > 0 && (
                      <ul className="blog__list">
                        {section.listAfter.map((item, j) => <li key={j}>{item}</li>)}
                      </ul>
                    )}
                    {section.comparison && (
                      <div className="blog__comparison">
                        <div className="blog__comparison-col">
                          <h3>{section.comparison.left.title}</h3>
                          <ul>
                            {section.comparison.left.list.map((item, j) => (
                              <li key={j}>{item}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="blog__comparison-col blog__comparison-col--accent">
                          <h3>{section.comparison.right.title}</h3>
                          <ul>
                            {section.comparison.right.list.map((item, j) => (
                              <li key={j}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {section.outro && (
                      <p className="blog__outro">{section.outro}</p>
                    )}
                  </section>
                ))}

                {article.myth && (
                  <div className="blog__myth">
                    <p className="blog__myth-heading">{article.myth.heading}</p>
                    <div className="blog__myth-row">
                      <div className="blog__myth-col blog__myth-col--myth">
                        <span className="blog__myth-label">{article.myth.mythLabel}</span>
                        <p>{article.myth.mythText}</p>
                      </div>
                      <div className="blog__myth-col blog__myth-col--reality">
                        <span className="blog__myth-label">{article.myth.realityLabel}</span>
                        <p>{article.myth.realityText}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="blog__cta">
                  <Link to="/contacts" className="blog__cta-btn">
                    {t('nav.contact', { ns: 'home' })}
                  </Link>
                </div>

              </article>
            </div>

            {relatedPosts.length > 0 && (
              <aside className="blog-article__sidebar">
                <h2 className="sidebar__title">{t('page.sidebar')}</h2>
                {relatedPosts.map((post) => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} className="sidebar__card">
                    {post.images.card && (
                      <div className="sidebar__card-img">
                        <img src={post.images.card} alt={post.title} loading="lazy" />
                      </div>
                    )}
                    <div className="sidebar__card-body">
                      <p className="sidebar__card-meta">
                        {post.date} &nbsp;·&nbsp; {post.readTime}
                      </p>
                      <h3 className="sidebar__card-title">{post.title}</h3>
                    </div>
                  </Link>
                ))}
              </aside>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default BlogArticle;
